import { useMemo } from 'react';
import {
  captureCandidateContext,
  deriveCandidateCredential,
} from 'candidate-app/candidateCapability';
// eslint-disable-next-line no-restricted-imports -- this hook IS the candidate-surface wrapper
import { useNavigate, type NavigateOptions } from 'react-router-dom';

import {
  buildRuntimePath,
  type NoParams,
  type PathParams,
  type QueryRecord,
} from '../url-registry/buildPath';

/**
 * Options accepted by `useCandidateNavigate`'s returned callback. Mirrors
 * `AppNavigateOptions` from `url-registry`: react-router-dom's
 * `NavigateOptions` (replace, state) plus a `hash` field. The leading `#` is
 * added automatically if missing.
 */
export type CandidateNavigateOptions = NavigateOptions & { hash?: string };

/**
 * Navigation callback shape: either a templated/runtime-string destination
 * (same contract as `useAppNavigate`) or a history delta like `nav(-1)`,
 * which candidate flows use for "go back".
 */
type CandidateNavigateFn = (<P extends string>(
  template: P,
  ...rest: NoParams<P> extends true
    ? [
        params?: PathParams<P>,
        query?: QueryRecord,
        options?: CandidateNavigateOptions,
      ]
    : [
        params: PathParams<P>,
        query?: QueryRecord,
        options?: CandidateNavigateOptions,
      ]
) => void) &
  ((delta: number) => void);

/**
 * Type-safe navigation for the candidate-facing interview app. The candidate
 * counterpart of `useAppNavigate` from `url-registry` — same `buildPath`
 * machinery and call shape, but over `candidatePaths`, which are deliberately
 * excluded from the staffing-chat route catalog (see `registry.ts`).
 *
 *   const navigate = useCandidateNavigate();
 *   navigate(candidatePaths.RESCHEDULE, { interviewId });
 *   navigate(candidatePaths.OPT_OUT_SUCCESS);
 *   navigate(backUrl);  // runtime string (e.g. ?back= round-trips)
 *   navigate(-1);       // history delta
 */
export function useCandidateNavigate(): CandidateNavigateFn {
  const navigate = useNavigate();
  return useMemo(() => {
    const candidateNavigate = (
      template: string | number,
      params?: Readonly<Record<string, string>>,
      query?: QueryRecord,
      options?: CandidateNavigateOptions
    ) => {
      if (typeof template === 'number') {
        navigate(template);
        return;
      }
      const { hash, ...navOpts } = options ?? {};
      // The public overloads have already checked required params.
      let url = buildRuntimePath(template, params, query);
      const context = captureCandidateContext();
      if (
        context?.kind === 'session' &&
        url.startsWith('/') &&
        !url.startsWith('//')
      ) {
        const destination = new URL(url, window.location.origin);
        const targetCredential = deriveCandidateCredential(
          destination.pathname,
          null
        );
        const currentCredential = deriveCandidateCredential(
          window.location.pathname,
          null
        );
        const opensAnotherInvitation =
          targetCredential?.kind === 'web_call_link' &&
          (currentCredential?.kind !== 'web_call_link' ||
            currentCredential.encoded_id !== targetCredential.encoded_id);
        if (
          !opensAnotherInvitation &&
          !destination.searchParams.has('interviewSession')
        ) {
          destination.searchParams.set('interviewSession', context.session_id);
        }
        url = `${destination.pathname}${destination.search}${destination.hash}`;
      }
      const target = hash
        ? `${url}${hash.startsWith('#') ? hash : `#${hash}`}`
        : url;
      navigate(target, navOpts);
    };
    // One body serves both public shapes; the return type exposes them.
    return candidateNavigate;
  }, [navigate]);
}
